import React from 'react';
import { getResidents } from '@/api/residents';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import type { User } from '@/types/v2/user';
import { useUser } from '@/hooks/v2/use-user';
import { Seo } from '@/components/seo';

import { ResidentsTable } from './components/residents-table';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react';

export function ResidentsPage(): React.JSX.Element {
  const { community } = useUser();
  const theme = useTheme();

  const { data, isLoading, refetch } = useQuery<{ residents: User[] }>({
    queryKey: ['residents', community?.id],
    queryFn: async () => {
      const response = await getResidents(community?.id, {
        include: 'units,communityRoles',
        all: true,
      });
      return response;
    },
    retry: false,
  });

  const handleCreate = React.useCallback(() => {
    refetch();
  }, [refetch]);

  if (!community?.id) {
    return <Typography variant="h6">No community found</Typography>;
  }

  return (
    <>
      <Seo title="Users" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            icon={<UsersThreeIcon size={36} />}
            subtitle="Manage resident information, view unit assignments, and track community membership"
            title="Users"
          />

          {/* Users Table */}
          <ResidentsTable
            communityId={community.id}
            loading={isLoading}
            onCreated={handleCreate}
            rows={data?.residents || []}
          />
        </Container>
      </Box>
    </>
  );
}
